import styled, { css, keyframes } from 'styled-components';
import { theme } from '../../styles/theme';
import { MessageRole } from '../../types/chat';

const popIn = keyframes`
  0% {
    transform: scale(0.6) translateY(20px);
    opacity: 0;
  }
  70% {
    transform: scale(1.1) translateY(-5px);
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
`;

interface BubbleStyleProps {
  $role: MessageRole;
  $isNew?: boolean;
  $isAction?: boolean;
}

const getBubbleStyles = ({ $role, $isAction }: BubbleStyleProps) => {
  if ($role === 'agent' && $isAction) {
    return css`
      background: rgba(0, 0, 0, 0.6);
      color: ${theme.colors.washi};
      border: none;
      border-radius: 12px;
      margin: 0 auto;
      max-width: 90%;
      font-style: italic;
      font-size: 0.95rem;
      padding: ${theme.spacing.sm} ${theme.spacing.lg};
      transform-origin: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    `;
  }

  switch ($role) {
    case 'user':
      return css`
        background: rgba(255, 255, 255, 0.95);
        border: 2px solid #000;
        border-radius: 24px 24px 24px 4px;
        margin-right: auto;
        transform-origin: bottom left;
        
        &::before {
          content: '';
          position: absolute;
          left: -2px;
          bottom: -2px;
          width: 26px;
          height: 26px;
          background: rgba(255, 255, 255, 0.95);
          border-left: 2px solid #000;
          border-bottom: 2px solid #000;
          border-radius: 0 0 0 26px;
          clip-path: polygon(0 0, 100% 0, 0 100%, 0 100%, 0 100%);
        }
      `;
    case 'agent':
      return css`
        background: rgba(255, 255, 255, 0.95);
        border: 2px solid #000;
        border-radius: 24px 24px 4px 24px;
        margin-left: auto;
        transform-origin: bottom right;
        
        &::before {
          content: '';
          position: absolute;
          right: -2px;
          bottom: -2px;
          width: 26px;
          height: 26px;
          background: rgba(255, 255, 255, 0.95);
          border-right: 2px solid #000;
          border-bottom: 2px solid #000;
          border-radius: 0 0 26px 0;
          clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 100% 100%);
        }
      `;
    case 'system':
      return css`
        background: rgba(255, 255, 255, 0.9);
        border: 2px dashed #000;
        border-radius: 30px;
        margin: 0 auto;
        font-style: italic;
        transform-origin: center;
        max-width: 60%;
        
        &::before,
        &::after {
          content: '';
          position: absolute;
          background: rgba(255, 255, 255, 0.9);
          border: 2px dashed #000;
          border-radius: 50%;
        }
        
        &::before {
          width: 10px;
          height: 10px;
          bottom: -16px;
          left: 50%;
          transform: translateX(-50%);
        }
        
        &::after {
          width: 6px;
          height: 6px;
          bottom: -26px;
          left: 50%;
          transform: translateX(-50%);
        }
      `;
  }
};

const Container = styled.div<BubbleStyleProps>`
  position: relative;
  max-width: 70%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${theme.colors.sumi};
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  animation: 
    ${({ $isNew }) => $isNew ? css`
      ${popIn} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards,
      ${float} 3s ease-in-out infinite
    ` : css`
      ${float} 3s ease-in-out infinite
    `};
  
  ${getBubbleStyles}
`;

interface MangaBubbleProps {
  role: MessageRole;
  isNew?: boolean;
  children: React.ReactNode;
}

export function MangaBubble({ role, isNew, children }: MangaBubbleProps) {
  const isAction = typeof children === 'string' && children.startsWith('action:');
  const content = typeof children === 'string' 
    ? children.replace(/^(action:|dialogue:)/, '').trim()
    : children;

  return (
    <Container $role={role} $isNew={isNew} $isAction={isAction}>
      {content}
    </Container>
  );
} 